import { Alarm, Clear } from '@mui/icons-material';
import { Divider, FormGroup, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useFormFields } from 'libs/hooksLib';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

import CustomButton from 'common/components/CustomButton';
import CustomIconButton from 'common/components/CustomIconButton';
import CustomTextValidator from 'common/components/CustomTextValidators';
import useFlexBoxStyles from 'common/flexBox';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    h5: {
        height: theme.spacing(5),
    },
    padding0: {
        '& .MuiInputBase-root': {
            padding: 0,
        },
    },
    eventInputSelector: {
        margin: theme.spacing(0.5),
        backgroundColor: 'none',
        '& > :first-of-type': {
            fontSize: theme.typography.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            width: 'auto',
            borderRadius: '0 !important',
            borderLeft: `1px solid ${theme.palette.secondary.light} !important`,
            '& > fieldset': {
                border: 'none !important',
            },
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
            overflow: 'hidden',
        },
        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary.light,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: theme.palette.default.main,
        },
        '& input': {
            position: 'absolute !important',
        },
    },
}));

const CalendarNotificationReminders = (props) => {
    const { reminders, onChange, disabled } = props;
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const { classes: flexBox } = useFlexBoxStyles();

    const [fields, handleFieldChange, resetForm] = useFormFields({
        time: 0,
        unit: 'h',
    });

    const handleAddReminderSubmit = (e) => {
        e.preventDefault();
        if (fields.time > 0) {
            onChange([...reminders, fields]);
            resetForm({
                time: 0,
                unit: 'h',
            });
        }
    };

    const deleteReminder = (index) => {
        if (index >= 0 && index < reminders.length) {
            onChange([...reminders.slice(0, index), ...reminders.slice(index + 1, reminders.length)]);
        }
    };

    const onTimeChange = (e) => {
        if (e.target.value > 0) {
            handleFieldChange(e);
        }
    };

    return (
        <Stack direction="column" spacing={1}>
            <ValidatorForm
                onSubmit={handleAddReminderSubmit}
                onError={() => {}}
                className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden, flexBox.h100)}
            >
                <FormGroup>
                    <Typography className={classes.text}>
                        {t('appointment.form.reminders')}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" className={cx(flexBox.w100)}>
                        <div className={cx(flexBox.w75, flexBox.marginRight2)}>
                            <CustomTextValidator
                                name="time"
                                fullWidth={true}
                                variant="outlined"
                                size="small"
                                type={'number'}
                                min={0}
                                useRegExpText={false}
                                value={fields.time}
                                onChange={(e) => onTimeChange(e)}
                                validators={[`minNumber:0`, 'required']}
                                errorMessages={[t('event.eventFormReminders.minValue'), t('app.common.required')]}
                                InputProps={{
                                    endAdornment: (
                                        <FormGroup>
                                            <TextField
                                                name="unit"
                                                select
                                                className={classes.eventInputSelector}
                                                value={fields.unit}
                                                onChange={handleFieldChange}
                                                variant="outlined"
                                                size="small"
                                            >
                                                <MenuItem key="hour" value="h">
                                                    {t('event.eventFormReminders.hours').toUpperCase()}
                                                </MenuItem>
                                                <MenuItem key="minute" value="m">
                                                    {t('event.eventFormReminders.minutes').toUpperCase()}
                                                </MenuItem>
                                            </TextField>
                                        </FormGroup>
                                    ),
                                }}
                            />
                        </div>

                        <CustomButton variant="contained" color="primary" type="submit" disabled={disabled}>
                            <span>{t('event.eventFormActors.add')}</span>
                        </CustomButton>
                    </Stack>
                </FormGroup>
            </ValidatorForm>

            {reminders?.map((reminder, index) => {
                return (
                    <Stack key={`reminder-${index}`} direction="column" spacing={1}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            className={flexBox.overflowHidden}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                className={cx(flexBox.overflowHidden, flexBox.w70, classes.h5)}
                            >
                                <Alarm />

                                <Stack direction="column" className={flexBox.overflowHidden}>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        {`${reminder.time}${reminder.unit} before`}
                                    </Typography>
                                </Stack>
                            </Stack>

                            <CustomIconButton size="small" onClick={() => deleteReminder(index)} disabled={disabled}>
                                <Clear />
                            </CustomIconButton>
                        </Stack>

                        {index + 1 < reminders.length && <Divider />}
                    </Stack>
                );
            })}
        </Stack>
    );
};

export default CalendarNotificationReminders;